<template>
  <aside
    class="hidden min-h-[calc(100vh-88px)] min-w-[292px] lg:block bg-grey-5"
    @wheel.prevent
    @touchmove.prevent
    @scroll.prevent
  >
    <!-- The panel is pinned top-0 on every route so the grey column is unbroken;
         MasterLayout's toolbar is commented out, so offsetting the panel by
         navbar-height left that 88px band rendering white above the sidebar.
         Routes that hide the profile header pad by its height instead (72px of
         padding + the 56px avatar row), so the nav buttons sit at the same y on
         every page rather than jumping when you leave home. -->
    <div
      :class="hidesProfileHeader ? 'pt-[128px]' : 'pt-0'"
      class="hidden fixed top-0 bottom-0 left-0 w-[292px] flex-col px-[24px] pb-[40px] lg:flex bg-grey-5 overflow-y-auto no-scrollbar z-30"
      style="box-shadow: 0px 1px 20px rgba(0, 0, 0, 0.1)"
    >
      <section
        v-if="isIndexPage() || isGPCPage()"
        class="pt-[72px] flex flex-row justify-between items-center"
      >
        <img
          v-if="isGPCPage()"
          src="../../../public/assets/icons/GENERATIONS.svg"
          class="home-logo w-auto"
          alt="GenerationS"
        />
        <template v-else>
          <div
            :class="[
              'rounded-full w-[56px] h-[56px] border-white border-[2px] relative',
              isGICConfType ? 'bg-orange' : 'bg-lightblue',
            ]"
          >
            <div
              v-if="isDropdownVisible"
              class="bg-black absolute rounded-full w-[51px] h-[51px] opacity-30"
            ></div>
            <button v-if="showProfilePic" class="side-profile-icon" @click="toggleDropdown">
              <img :src="profilePicture" class="rounded-full" />
            </button>
            <button
              v-if="!showProfilePic"
              class="side-profile-icon text-[#FFF] text-[28px] w-[56px] h-[56px] font-family-['aktiv-grotesk-medium'] text-center leading-loose"
              @click="toggleDropdown"
            >
              {{ myName.charAt(0) }}
            </button>

            <div
              v-if="isDropdownVisible"
              v-click-away="removeDropdown"
              class="w-[144px] mt-[16px] relative py-[15px] bg-white rounded-[12px] inline-flex flex-col items-start shadow-lg"
            >
              <router-link to="/profile">
                <div
                  class="flex px-[12px] h-[20px] items-center gap-[8px] self-stretch text-grey-100 text-[14px] font-[400] leading-[20px]"
                  data-event-tracking="gensapp-profile-viewprofile"
                  @click="logEvent('gensapp-profile-viewprofile')"
                >
                  <img
                    src="../../../public/assets/icons/profile-circle.svg"
                    data-event-tracking="gensapp-profile-viewprofile"
                    @click="logEvent('gensapp-profile-viewprofile')"
                  />
                  <div
                    data-event-tracking="gensapp-profile-viewprofile"
                    @click="logEvent('gensapp-profile-viewprofile')"
                  >
                    {{ $t('copy.homePage.profileDropdown.profile') }}
                  </div>
                </div>
              </router-link>
              <router-link to="/gpcr/language" v-if="isGPCRPage()">
                <div
                  class="flex px-[12px] w-[163px] h-[20px] items-center gap-[8px] mt-[12px] self-stretch text-grey-100 text-[14px] z-50"
                >
                  <img src="../../../public/assets/icons/language-icon.svg" />{{
                    $t('copy.homePage.profileDropdown.language')
                  }}
                </div>
              </router-link>
              <router-link
                :to="conferenceRoute"
                v-if="hasMultipleConferenceTypes(confYear, conferences)"
                class="flex px-[12px] h-[20px] items-center gap-[8px] mt-[12px] self-stretch text-grey-100 text-[14px] font-[400] leading-[20px] cursor-pointer"
              >
                <img src="../../../public/assets/icons/conference-icon.svg" />
                <div>
                  {{ $t('copy.homePage.profileDropdown.conference') }}
                </div>
              </router-link>
              <div
                class="flex px-[12px] h-[20px] items-center gap-[8px] mt-[12px] self-stretch text-[#B80A0A] text-[14px] font-[400] leading-[20px] cursor-pointer"
                data-event-tracking="gensapp-home-logout-text"
                data-test-id="gensapp-home-logout-text"
                @click="openModal()"
              >
                <img src="../../../public/assets/icons/log-out-black.svg" />
                {{ $t('copy.homePage.profileDropdown.logout') }}
              </div>
            </div>
          </div>

          <div class="w-[176px] text-[20px] ml-3 leading-[24px] font-aktiv-grotesk-trial">
            {{ myName }}
          </div>
        </template>
      </section>
      <!-- Same margin on every route: combined with the panel's stand-in padding
           this keeps the nav buttons at one fixed y across all GIC/GPCR pages. -->
      <section class="mt-4">
        <OutlineButton
          v-for="(link, i) in announcementIconSwap()"
          :key="i"
          :icon="link.icon"
          :to="link.to"
          :active-path="link.activePath"
          :label="link.label"
          :is-external="link.isExternal"
          :data-event-tracking="link.dataEventTracking"
          :data-test-id="link.dataTestId"
          :is-gic="isGICConfType"
          class="mb-[12px]"
          @clicked-button="onNavClick()"
        />
      </section>

      <section class="mt-auto flex flex-col justify-center items-center">
        <button
          v-if="isGPCPage()"
          class="mb-[16px] text-[#B80A0A] text-[14px] font-[600] leading-[20px] cursor-pointer"
          data-event-tracking="gensapp-sidebar-logout-text"
          data-test-id="gensapp-sidebar-logout-text"
          @click="openModal()"
        >
          {{ $t('copy.homePage.profileDropdown.logout') }}
        </button>
        <div class="flex-container space-x-3">
          <a
            class="text-[14px] leading-[20px] text-grey-60 hover:underline"
            href="/terms-of-use"
            target="_blank"
            data-event-tracking="gensapp-menu-termsofuse"
            data-test-id="gensapp-menu-termsofuse"
            >{{ $t('copy.sideBar.termOfUse') }}</a
          >
          <a
            class="text-[14px] leading-[20px] text-grey-60 hover:underline"
            href="/privacy-policy"
            target="_blank"
            data-event-tracking="gensapp-menu-privacypolicy"
            data-test-id="gensapp-menu-privacypolicy"
            >{{ $t('copy.sideBar.privacyPolicy') }}</a
          >
        </div>
      </section>
    </div>
  </aside>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import OutlineButton from './OutlineButton.vue';
import OutlineHomeIcon from '@/components/icons/OutlineHome';
import HomeGICIcon from '@/components/icons/HomeGIC';
import OutlineScheduleIcon from '@/components/icons/OutlineSchedule';
import ScheduleGICIcon from '@/components/icons/ScheduleGIC';
import OutlineTeamIcon from '@/components/icons/OutlineTeam';
import OutlineResourcesIcon from '@/components/icons/OutlineResources';
import ResourcesGICIcon from '@/components/icons/ResourcesGIC';
import OutlineAnnouncementIcon from '@/components/icons/OutlineAnnouncement';
import OutlineUnreadAnnouncementIcon from '@/components/icons/OutlineUnreadAnnouncement';
import { mixin as clickaway } from 'vue3-click-away';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import {
  CONFERENCE_TYPES,
  isGPCRPage,
  isGICPage,
  getCurrentConferenceType,
  hasMultipleConferenceTypes,
} from '@/utils/conference-type';
import { getCurrentLocale } from '@/utils/deviceLocale';
import { buildPhotodropMementoUrl, hasMementoAccess } from '@/utils/memento';

export default {
  mixins: [clickaway],
  components: {
    OutlineButton,
  },
  data() {
    return {
      selectedConfType: CONFERENCE_TYPES.REGIONAL,
      isDropdownVisible: false,
    };
  },
  computed: {
    ...mapGetters('profile', ['name', 'isAbsentAlumni']),
    ...mapGetters('announcements', ['getUnreadCount']),
    ...mapState('social', ['profilePicture']),
    ...mapState('social', {
      displayName: 'name',
    }),
    ...mapState('profile', [
      'confYear',
      'conferences',
      'hasOldPhotodrop',
      'email',
      'country',
      'groupName',
    ]),
    // name is the raw profile state (DB name column), NOT the profile `name`
    // getter above — memento must receive the full name, never the preferred name.
    ...mapState('profile', { rawName: 'name' }),
    isGICConfType() {
      return getCurrentConferenceType(this.confYear, this.conferences) === 'GIC';
    },
    // Exact inverse of the profile header section's own v-if, so the two cannot
    // drift apart: when that section is absent the panel pads by its height.
    hidesProfileHeader() {
      return !this.isIndexPage() && !this.isGPCPage();
    },
    myName() {
      return this.displayName ? this.displayName : this.name;
    },
    showProfilePic() {
      return this.profilePicture;
    },
    conferenceRoute() {
      if (this.isGICConfType) return '/gic/conference';
      if (isGPCRPage()) return '/gpcr/conference';
      return '/conference';
    },
    // Alumni delegates have no current conference (so no schedule): keep PhotoDrop
    // in the middle slot for them, as before. Everyone else gets Schedule.
    //
    // PhotoDrop destination for absent alumni depends on two independent facts:
    // - hasMementoAccess: attended GPC 2026 (has something new to view)
    // - hasOldPhotodrop: linked to an OLD_CONF_IDS conference (has history to browse)
    // Both → the welcome page (offers both options). Only 2026 access, no old
    // history → skip the welcome page entirely and go straight to the external
    // Memento link, since "Browse past years" would have nothing to show anyway.
    // Neither, or only old history → the old gallery.
    scheduleOrPhotodropLink() {
      if (this.isAbsentAlumni) {
        const base = {
          label: 'PhotoDrop',
          icon: '/assets/icons/navbar-photdrop-hollow.svg',
          dataEventTracking: 'gensapp-menu-schedule',
          dataTestId: 'gensapp-sidebar-menu-photodrop',
        };
        const hasAccess = hasMementoAccess(this.conferences);

        if (hasAccess && this.hasOldPhotodrop) {
          return { ...base, to: '/photodrop/welcome', activePath: '/photodrop/welcome' };
        }
        if (hasAccess) {
          return {
            ...base,
            isExternal: true,
            to: buildPhotodropMementoUrl({
              name: this.rawName,
              email: this.email,
              country: this.country,
              groupName: this.groupName,
            }),
          };
        }
        return { ...base, to: '/photodrop', activePath: '/photodrop' };
      }
      return {
        to: '/schedule',
        activePath: '/schedule',
        label: this.$t('copy.sideBar.schedule'),
        icon: '/assets/icons/navbar-schedule-hollow.svg',
        dataEventTracking: 'gensapp-menu-schedule',
        dataTestId: 'gensapp-sidebar-menu-schedule',
      };
    },
    gpcLinks() {
      const allLinks = [
        {
          to: '/home',
          label: this.$t('copy.sideBar.home'),
          icon: '/assets/icons/navbar-home-hollow.svg',
          dataEventTracking: 'gensapp-menu-home',
          dataTestId: 'gensapp-sidebar-menu-home',
        },
        {
          to: '/resources/sessions',
          label: this.$t('copy.sideBar.resources'),
          icon: '/assets/icons/navbar-resources-hollow.svg',
          dataEventTracking: 'gensapp-menu-resources',
          dataTestId: 'gensapp-sidebar-menu-resources',
        },
        this.scheduleOrPhotodropLink,
        {
          to: '/profile',
          label: 'Profile',
          icon: '/assets/icons/navbar-profile-hollow.svg',
          dataEventTracking: 'gensapp-menu-profile',
          dataTestId: 'gensapp-sidebar-menu-profile',
        },
        {
          to: '/more',
          label: 'More',
          icon: '/assets/icons/navbar-more-hollow.svg',
          dataEventTracking: 'gensapp-menu-more',
          dataTestId: 'gensapp-sidebar-menu-more',
        },
      ];
      return allLinks;
    },
    gpcrLinks() {
      const allLinks = [
        {
          to: '/gpcr/home',
          label: this.$t('copy.sideBar.home'),
          icon: OutlineHomeIcon,
          dataEventTracking: 'gensapp-menu-home',
          dataTestId: 'gensapp-sidebar-menu-home',
        },
        {
          to: '/gpcr/resources/sessions',
          label: this.$t('copy.sideBar.resources'),
          icon: OutlineResourcesIcon,
          dataEventTracking: 'gensapp-menu-resources',
          dataTestId: 'gensapp-sidebar-menu-resources',
        },
        {
          to: '/gpcr/schedule',
          label: this.$t('copy.sideBar.schedule'),
          icon: OutlineScheduleIcon,
          dataEventTracking: 'gensapp-menu-schedule',
          dataTestId: 'gensapp-sidebar-menu-schedule',
        },
        {
          to: '/gpcr/team',
          label: this.$t('copy.sideBar.team'),
          icon: OutlineTeamIcon,
          dataEventTracking: 'gensapp-menu-team',
          dataTestId: 'gensapp-sidebar-menu-team',
        },
        {
          to: '/gpcr/announcement',
          label: this.$t('copy.sideBar.updates'),
          icon:
            this.getUnreadCount(CONFERENCE_TYPES.REGIONAL) > 0
              ? OutlineUnreadAnnouncementIcon
              : OutlineAnnouncementIcon,
          dataEventTracking: 'gensapp-menu-announcement',
          dataTestId: 'gensapp-sidebar-menu-announcement',
        },
      ];
      return allLinks;
    },
    gicLinks() {
      const allLinks = [
        {
          to: '/gic/home',
          label: this.$t('copy.sideBar.home'),
          icon: HomeGICIcon,
          dataEventTracking: 'gensapp-menu-home',
          dataTestId: 'gensapp-sidebar-menu-home',
        },
        {
          to: '/gic/resources/sessions',
          label: this.$t('copy.sideBar.resources'),
          icon: ResourcesGICIcon,
          dataEventTracking: 'gensapp-menu-resources',
          dataTestId: 'gensapp-sidebar-menu-resources',
        },
        {
          to: '/gic/schedule',
          label: this.$t('copy.sideBar.schedule'),
          icon: ScheduleGICIcon,
          dataEventTracking: 'gensapp-menu-schedule',
          dataTestId: 'gensapp-sidebar-menu-schedule',
        },
      ];
      return allLinks;
    },
  },
  // do it 1 time only it loaded the page
  async mounted() {
    const conferenceType = getCurrentConferenceType(this.confYear, this.conferences);
    const locale = await getCurrentLocale(conferenceType);
    this.$i18n.locale = locale;
    document.querySelector('html').setAttribute('lang', locale);
  },
  methods: {
    ...mapActions('schedule', ['setLinkOpenDate', 'setScrollPosition']),
    hasMultipleConferenceTypes,
    openModal() {
      this.isDropdownVisible = false;
      this.$emit('open-modal');
    },
    isIndexPage() {
      return (
        this.$route.path === '/home' ||
        this.$route.path === '/gpcr/home' ||
        this.$route.path === '/gic/home'
      );
    },
    isGPCPage() {
      return !isGPCRPage() && !isGICPage();
    },
    onNavClick() {
      this.clearSchedule();
    },
    clearSchedule() {
      this.setLinkOpenDate('');
      this.setScrollPosition(1);
    },
    announcementIconSwap() {
      if (this.isGICConfType) return this.gicLinks;
      if (isGPCRPage()) return this.gpcrLinks;
      return this.gpcLinks;
    },
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },
    removeDropdown() {
      this.isDropdownVisible = false;
    },
    logEvent(event) {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: event,
          page: 'home',
        },
      });
    },
    isGPCRPage() {
      return isGPCRPage();
    },
  },
};
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.show {
  display: block;
}

.home-logo {
  height: clamp(40px, 10vw, 56px);
}
</style>
